import { Routes, Route, Navigate } from "react-router-dom";
import { Suspense, lazy } from "react";
import { LoadingScreen } from "@/components/LoadingScreen";
import { RequireAdmin, RequireParent } from "@/components/auth-guards";

// Public
const Home = lazy(() => import("@/pages/public/Home"));
const Enrol = lazy(() => import("@/pages/public/Enrol"));
const EnrolSuccess = lazy(() => import("@/pages/public/EnrolSuccess"));

// Admin
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const ForgotPassword = lazy(() => import("@/pages/admin/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/admin/ResetPassword"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const FamilyList = lazy(() => import("@/pages/admin/FamilyList"));
const FamilyDetail = lazy(() => import("@/pages/admin/FamilyDetail"));
const Assessments = lazy(() => import("@/pages/admin/Assessments"));
const Roster = lazy(() => import("@/pages/admin/Roster"));
const Payments = lazy(() => import("@/pages/admin/Payments"));
const TermOverview = lazy(() => import("@/pages/admin/TermOverview"));
const Notices = lazy(() => import("@/pages/admin/Notices"));
const Settings = lazy(() => import("@/pages/admin/Settings"));
const AnnualReport = lazy(() => import("@/pages/admin/AnnualReport"));
const TermInvoicing = lazy(() => import("@/pages/admin/TermInvoicing"));
const PendingApprovals = lazy(() => import("@/pages/admin/PendingApprovals"));
const TestDataCleanup = lazy(() => import("@/pages/admin/TestDataCleanup"));

// Parent self-service
const ParentLogin = lazy(() => import("@/pages/parent/ParentLogin"));
const ParentDashboard = lazy(() => import("@/pages/parent/ParentDashboard"));
const ParentProfile = lazy(() => import("@/pages/parent/ParentProfile"));

const NotFound = lazy(() => import("@/pages/public/NotFound"));

export default function App() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* Public */}
        <Route path="/" element={<Home />} />
        <Route path="/enrol" element={<Enrol />} />
        <Route path="/enrol/success" element={<EnrolSuccess />} />

        {/* Parent self-service */}
        <Route path="/parent/login" element={<ParentLogin />} />
        <Route
          path="/parent"
          element={
            <RequireParent>
              <ParentDashboard />
            </RequireParent>
          }
        />
        <Route
          path="/parent/profile"
          element={
            <RequireParent>
              <ParentProfile />
            </RequireParent>
          }
        />

        {/* Admin */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/forgot-password" element={<ForgotPassword />} />
        <Route path="/admin/reset-password" element={<ResetPassword />} />
        <Route
          path="/admin"
          element={
            <RequireAdmin>
              <AdminLayout />
            </RequireAdmin>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="families" element={<FamilyList />} />
          <Route path="families/:fuid" element={<FamilyDetail />} />
          <Route path="assessments" element={<Assessments />} />
          <Route path="roster" element={<Roster />} />
          <Route path="payments" element={<Payments />} />
          <Route path="term-overview" element={<TermOverview />} />
          <Route path="annual-report" element={<AnnualReport />} />
          <Route path="term-invoicing" element={<TermInvoicing />} />
          <Route path="pending-approvals" element={<PendingApprovals />} />
          <Route path="notices" element={<Notices />} />
          <Route path="settings" element={<Settings />} />
          <Route path="test-data" element={<TestDataCleanup />} />
        </Route>

        {/* Convenience redirects */}
        <Route path="/login" element={<Navigate to="/admin/login" replace />} />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
